import React from 'react';
import { ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  BUDGET_LEGEND_TITLE,
  BUDGET_UNASSIGNED_HELP,
  BUDGET_UNASSIGNED_LABEL,
  budgetTreatmentHelp,
  translateBudgetTreatment,
} from '@/lib/translations';
import { treatmentsForCategoryType } from '@/lib/utils/budget';

export interface BudgetLegendProps {
  // Open while nothing is classified yet: that is when the vocabulary is new and every row needs it.
  defaultOpen?: boolean;
}

const GROUPS = [
  { type: 'income', title: 'Receitas' },
  { type: 'expense', title: 'Despesas' },
] as const;

// One place that says what each treatment means, instead of a tooltip per pill: Radix tooltips don't reliably
// open on touch. Grouped by category type, so it also restates which treatments each type accepts.
export const BudgetLegend: React.FC<BudgetLegendProps> = ({ defaultOpen = false }) => (
  <Collapsible defaultOpen={defaultOpen} className="mt-4 border-y border-border/60">
    <CollapsibleTrigger className="group flex w-full items-center gap-1.5 py-3 text-sm text-muted-foreground transition-colors hover:text-foreground">
      {BUDGET_LEGEND_TITLE}
      <ChevronDown className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180" aria-hidden="true" />
    </CollapsibleTrigger>
    <CollapsibleContent>
      <div className="grid gap-6 pb-4 sm:grid-cols-2">
        {GROUPS.map(({ type, title }) => (
          <div key={type}>
            <h3 className="mb-2 text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">{title}</h3>
            <dl className="space-y-2">
              {treatmentsForCategoryType(type).map((treatment) => (
                <div key={treatment}>
                  <dt className="text-sm font-medium text-foreground">{translateBudgetTreatment(treatment)}</dt>
                  <dd className="text-xs text-muted-foreground">{budgetTreatmentHelp(treatment)}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
      <dl className="pb-4">
        <dt className="text-sm font-medium text-foreground">{BUDGET_UNASSIGNED_LABEL}</dt>
        <dd className="text-xs text-muted-foreground">{BUDGET_UNASSIGNED_HELP}</dd>
      </dl>
    </CollapsibleContent>
  </Collapsible>
);
